// YtreYouTubeConnectCard.jsx — Iter160d (Randy pt-ytre-p1).
//
// A slim, YTRE-branded status card for the YouTube channel connection.
// Purpose:
//   * Show the user whether their YouTube channel is connected on THIS
//     silo (so YTRE can post the master reply and read the currently-
//     open comment).
//   * If unconnected, offer a compact CTA that opens the SAME setup
//     modal the YT Replies module uses — no double-configuring, no
//     duplicate credential paste.
//
// Cross-link contract (backend, verified):
//   * The OAuth pair is stored under (user_id, silo_id, channel_slot).
//     `channel_slot="channel1"` is shared between Workbench YT Replies
//     and YTRE. Filling out one auto-fills the other, because both
//     read/write the same Mongo row.
//   * The setup modal itself is channel-agnostic — its content is a
//     generic "how to create Google OAuth credentials + connect" walk-
//     through. YTRE mounts it with channelSlot="channel1".
//
// UI intentionally MINIMAL — no ad copy, no sample chat, no SKU pill,
// no Manage button. Just "Connect / Reconnect" and a status line.
// If the user needs the deeper Workbench replies console, that lives
// on the Workbench tab; this card just owns the connection state.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlugCharging, CheckCircle, WarningCircle, LinkSimple } from "@phosphor-icons/react";
import { ytApi } from "@/lib/ytApi";
import YTRepliesSetupModal from "@/components/YTRepliesSetupModal";
import { YtreChannelPicker } from "@/components/YtreChannelPicker";

// Iter160w (Randy pt-yt-refresh-cadence, Feb 2026) — long editing sessions
// were flooding /youtube/integration/... with getIntegration calls because
// every window `focus` event fired a full refresh. Randy tabs constantly
// between YTRE + the YouTube video + Workbench during a session, which was
// hitting the backend ~50-100x per hour for zero material state change.
// Fix: cheap client-side throttle — swallow focus refreshes that fire less
// than THROTTLE_MS after the previous one. The user-clicked "Reconnect"
// path is untouched (calls refresh() directly, not through the throttle).
const FOCUS_REFRESH_THROTTLE_MS = 30_000;

export default function YtreYouTubeConnectCard({ siloId }) {
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const lastRefreshAt = useRef(0);

  const refresh = async () => {
    if (!siloId) return;
    try {
      setLoading(true);
      const row = await ytApi.getIntegration(siloId, "channel1");
      setIntegration(row || null);
      lastRefreshAt.current = Date.now();
    } catch { setIntegration(null); }
    finally { setLoading(false); }
  };

  const maybeRefresh = () => {
    if (Date.now() - lastRefreshAt.current < FOCUS_REFRESH_THROTTLE_MS) return;
    refresh();
  };

  useEffect(() => { refresh(); }, [siloId]);

  // Iter160e (Randy) — the connection card was going stale after a
  // Load-Gate mirror or after the user returned from an OAuth handshake
  // in a new tab. Two auto-refresh hooks patch that:
  //
  //   1. When the tab regains focus / visibility → re-check (THROTTLED
  //      to at most once per 30s so long editing sessions don't spam
  //      the backend — Iter160w).
  //   2. While we're in the "keys saved but no channel connected" state
  //      → poll every 3s so the moment the redirect writes the channel
  //      row, the card lights up green. Auto-stops as soon as the
  //      integration flips connected.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) maybeRefresh(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [siloId]);

  const waitingForOauth = !!integration?.google_client_id && !integration?.youtube_channel_id;
  useEffect(() => {
    if (!waitingForOauth) return;
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [waitingForOauth, siloId]);

  const connected = !!integration?.youtube_channel_id;
  const hasKeys = !!integration?.google_client_id;

  const disconnect = async () => {
    if (!connected) return;
    if (!window.confirm(`Disconnect ${integration.youtube_channel_name || "this YouTube channel"} from this silo? This clears the OAuth token so YTRE + YT Replies can't post until you reconnect. Your saved Google Cloud credentials AND your entire draft history stay preserved — reconnecting on this silo restores everything.`)) return;
    try {
      await ytApi.disconnect(siloId, "channel1");
      toast.success("YouTube channel disconnected · history preserved.");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed."); }
  };

  return (
    <div
      className="border hairline rounded-lg bg-white/60 px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-2 sm:gap-3 flex-wrap"
      data-testid="ytre-yt-connect-card"
    >
      {/* Left icon */}
      <div className="shrink-0">
        {loading ? (
          <span className="w-4 h-4 inline-block rounded-full bg-[#E4E6E1] animate-pulse" />
        ) : connected ? (
          <CheckCircle size={16} className="text-[#2F6B3A] sm:hidden" weight="fill" />
        ) : hasKeys ? (
          <WarningCircle size={16} className="text-[#B98A47] sm:hidden" weight="fill" />
        ) : (
          <PlugCharging size={16} className="text-[#6B6A68] sm:hidden" weight="duotone" />
        )}
        {!loading && (
          <>
            {connected && <CheckCircle size={18} className="text-[#2F6B3A] hidden sm:inline" weight="fill" />}
            {!connected && hasKeys && <WarningCircle size={18} className="text-[#B98A47] hidden sm:inline" weight="fill" />}
            {!connected && !hasKeys && <PlugCharging size={18} className="text-[#6B6A68] hidden sm:inline" weight="duotone" />}
          </>
        )}
      </div>

      {/* Status text — Iter160y (Randy pt-ytre-yt-card-mobile, Feb 2026):
          mobile hides the verbose subtitle and shortens the "YouTube
          channel · YTRE" eyebrow so the row fits comfortably on a
          small screen. Desktop keeps the full status copy. */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#6B6A68]">
          <span className="sm:hidden">YT · YTRE</span>
          <span className="hidden sm:inline">YouTube channel · YTRE</span>
        </div>
        {loading ? (
          <div className="text-[12px] text-[#6B6A68] italic">Checking connection…</div>
        ) : connected && integration?.needs_reauth ? (
          /* Sep 10 2026 (Randy — production logged `invalid_client` every 90s
             for two channels). A revoked or rotated Google secret is
             permanent: the poller now parks the integration instead of
             retrying forever, so the card has to say so or drafting just
             stops silently. */
          <div className="text-[12.5px] sm:text-[13px] text-[#8A1C1C] leading-tight" data-testid="ytre-yt-needs-reauth">
            Reconnect needed — <strong className="font-semibold">{integration.youtube_channel_name || integration.youtube_channel_id}</strong> stopped accepting our sign-in.
            <span className="hidden sm:block text-[10.5px] text-[#8A1C1C]/80 leading-tight mt-0.5">
              Google rejected the saved credentials{integration.auth_error ? ` (${integration.auth_error})` : ""}. Auto-drafting is paused for this channel until you reconnect. Your drafts and history are untouched.
            </span>
          </div>
        ) : connected ? (
          <div className="text-[12.5px] sm:text-[13px] text-[#1C1B1A] leading-tight">
            Connected to <strong className="font-semibold" data-testid="ytre-yt-channel-name">{integration.youtube_channel_name || integration.youtube_channel_id}</strong>
            {/* Sep 16 2026 (Randy: "I see no option to quick connect to the
                'other' auth youtube account") — the switcher belongs right
                next to the identity it replaces. Instant for any channel
                already on file; no Google window. */}
            <span className="ml-2 align-middle">
              <YtreChannelPicker
                siloId={siloId}
                connectedChannelId={integration.youtube_channel_id}
                onSwitched={() => refresh()}
                onConnectNew={() => setSetupOpen(true)}
                label="switch"
                testid="ytre-yt-card-switch"
              />
            </span>
            <span className="hidden sm:block text-[10.5px] text-[#6B6A68] leading-tight mt-0.5">
              Ready to post master replies. Same connection powers YT Replies in Workbench.
            </span>
          </div>
        ) : hasKeys ? (
          <div className="text-[12.5px] sm:text-[13px] text-[#1C1B1A] leading-tight">
            <span className="sm:hidden">Finish Google sign-in.</span>
            <span className="hidden sm:inline">Credentials saved — finish the Google sign-in to activate.</span>
            <span className="hidden sm:block text-[10.5px] text-[#6B6A68] leading-tight mt-0.5">
              Master replies can&apos;t post until the channel is fully connected.
            </span>
          </div>
        ) : (
          <div className="text-[12.5px] sm:text-[13px] text-[#1C1B1A] leading-tight">
            Not connected yet.
            <span className="hidden sm:block text-[10.5px] text-[#6B6A68] leading-tight mt-0.5">
              Connect your channel so YTRE can post the finished master reply. One setup covers YTRE and Workbench YT Replies (same OAuth row).
            </span>
          </div>
        )}
      </div>

      {/* Actions — Iter160y (Randy pt-ytre-yt-card-mobile): compact
          icon-forward buttons on mobile with shortened labels; full
          "Reconnect / edit" wording on desktop. BOTH reconnect and
          disconnect stay reachable on mobile per Randy's spec. */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        <button
          onClick={() => setSetupOpen(true)}
          data-testid="ytre-yt-connect-btn"
          className={`text-[11px] sm:text-[11.5px] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md flex items-center gap-1 sm:gap-1.5 ${
            connected
              ? "border border-[#E4E6E1] bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60"
              : "bg-[#B54848] text-white hover:bg-[#8f3838]"
          }`}
          title={connected ? "Reconnect or edit this YouTube channel" : hasKeys ? "Finish the OAuth handshake" : "Connect a YouTube channel"}
        >
          <LinkSimple size={11} weight="duotone" />
          <span className="sm:hidden">{connected ? "Edit" : hasKeys ? "Finish" : "Connect"}</span>
          <span className="hidden sm:inline">{connected ? "Reconnect / edit" : hasKeys ? "Finish connection" : "Connect YouTube"}</span>
        </button>
        {connected && (
          <button
            onClick={disconnect}
            data-testid="ytre-yt-disconnect-btn"
            className="text-[11px] sm:text-[11px] px-2.5 sm:px-0 py-1 sm:py-0 rounded-md sm:rounded-none border sm:border-0 border-[#E4E6E1] sm:border-none text-[#C16E5A] hover:bg-[#F5E5E1] sm:hover:bg-transparent sm:hover:underline"
            title="Disconnect this channel from the silo"
          >
            Disconnect
          </button>
        )}
      </div>

      {setupOpen && (
        <YTRepliesSetupModal
          open={setupOpen}
          onClose={() => { setSetupOpen(false); refresh(); }}
          channelSlot="channel1"
          siloId={siloId}
          surfaceTitle="AI YouTube YTRE — Setup"
        />
      )}
    </div>
  );
}
